/**
 * Black Trident – Holster Finder
 * Shopify Web Component
 *
 * Das HTML-Skeleton wird vom Liquid geliefert (holster-finder.liquid).
 * Dieses Script übernimmt ausschliesslich die Logik.
 *
 * Desktop-Panel: Tom Select, via [data-hf-input-set]
 * Mobile-Drawer: native <select>, registriert on drawer-open via [data-hf-filter-drawer]
 *
 * Voraussetzung: Tom Select muss vor diesem Script geladen sein.
 */

class HolsterFinder extends HTMLElement {
  connectedCallback() {
    this._indexUrl = this.getAttribute("index-url");
    this._ctaLabel = this.getAttribute("cta-label") || "Jetzt konfigurieren →";
    this._availableLabel = this.getAttribute("label-available");
    this._unavailableLabel = this.getAttribute("label-unavailable");
    this._notAvailableMessage = this.getAttribute("not-available-message");
    this._countLabelTotal =
      this.getAttribute("label-count-total") || "{{count}} Modelle";
    this._countLabelFound =
      this.getAttribute("label-count-found") || "{{count}} Modelle gefunden";
    this._countLabelHidden =
      this.getAttribute("label-count-hidden") || "- {{count}} ausgeblendet";
    this._countLabelZero =
      this.getAttribute("label-count-zero") || "Keine Modelle gefunden";
    this._selectionEmptyLabel =
      this.getAttribute("label-selection-empty") ||
      "Noch keine Waffe ausgewählt";
    this._selectionEmptyHint =
      this.getAttribute("label-selection-empty-hint") ||
      "Nach deiner Auswahl zeigen wir dir nur kompatible Holster.";
    this._noLightLabel = this.getAttribute("label-no-light") || "Kein Licht";
    this._emptyLabel =
      this.getAttribute("label-empty") ||
      "Kein Holster für {{selection}} in dieser Kategorie verfügbar.";

    this.isTouch = false;

    this._state = {
      index: null,
      manufacturer: "",
      model: "",
      light: "",
      lastEntries: [],
    };

    // Desktop Input-Sets (Tom Select)
    this._inputSets = [...this.querySelectorAll("[data-hf-input-set]")].map(
      (set) => ({
        manufacturer: set.querySelector("[data-hf-manufacturer]"),
        model: set.querySelector("[data-hf-model]"),
        light: set.querySelector("[data-hf-light]"),
        reset: set.querySelector("[data-hf-reset]"),
        filterActive: set.querySelector("[data-hf-active]"),
        submit: set.querySelector("[data-hf-submit]"),
      }),
    );

    // Fallback: kein data-hf-input-set vorhanden
    if (this._inputSets.length === 0) {
      this._inputSets = [
        {
          manufacturer: this.querySelector("[data-hf-manufacturer]"),
          model: this.querySelector("[data-hf-model]"),
          light: this.querySelector("[data-hf-light]"),
          reset: this.querySelector("[data-hf-reset]"),
          filterActive: this.querySelector("[data-hf-active]"),
          submit: this.querySelector("[data-hf-submit]"),
        },
      ];
    }

    // Einmalige Elemente
    this._els = {
      status: this.querySelector("[data-hf-status]"),
      cards: this.querySelector("[data-hf-cards]"),
    };

    this.statusDot = this.querySelector("[data-hf-status-dot]");

    // Tom Select Instanzen pro Desktop-Set
    this._ts = this._inputSets.map(() => ({
      manufacturer: null,
      model: null,
      light: null,
    }));

    // Event-Listener für Desktop-Sets
    this._inputSets.forEach((set, i) => {
      set.reset?.addEventListener("click", () => this._reset());
      set.filterActive?.addEventListener("change", (e) => {
        this._filterUnavailable(e.detail.checked);
        this._inputSets.forEach((s, j) => {
          if (j !== i && s.filterActive)
            s.filterActive.checked = e.detail.checked;
        });
      });
      set.submit?.addEventListener("click", () => this._findAndRender());
    });

    // Mobile Drawer — native selects, registriert on drawer-open
    this._drawerEl = this.querySelector("[data-hf-filter-drawer]");
    if (this._drawerEl) {
      this._drawerEl.addEventListener("drawer-open", () =>
        this._onDrawerOpen(),
      );
      this._drawerEl.addEventListener("drawer-close", () =>
        this._onDrawerClose(),
      );
    }

    if (this._isTouch()) {
      document.documentElement.classList.add("touch-device");
      this.isTouch = true;
    }

    this._loadData();
  }

  disconnectedCallback() {
    this._destroyAll();
  }

  _isTouch() {
    return "ontouchstart" in window;
  }

  /* ─── Drawer Handling ───────────────────────────────────────────── */

  _onDrawerOpen() {
    // Drawer klont den Inhalt — geklonte Selects im _body registrieren
    const body = this._drawerEl._body;
    if (!body) return;

    const mfEl = body.querySelector("[data-hf-manufacturer]");
    const moEl = body.querySelector("[data-hf-model]");
    const lEl = body.querySelector("[data-hf-light]");
    const resetEl = body.querySelector("[data-hf-reset]");
    const filterEl = body.querySelector("[data-hf-active]");

    // Aktuellen State in Drawer-Selects synchronisieren
    const st = this._state;
    if (mfEl) mfEl.value = st.manufacturer;
    if (moEl) moEl.value = st.model;
    if (lEl) lEl.value = st.light;

    // Optionen befüllen
    if (mfEl)
      this._populateNativeSelect(
        mfEl,
        this._manufacturerIndices()
          .map((i) => this._s(i))
          .sort((a, b) => a.localeCompare(b, "de")),
        mfEl.getAttribute("data-placeholder") || "Hersteller auswählen",
        false,
        st.manufacturer,
      );

    if (st.manufacturer) {
      const mIdx = this._getManufacturerIdx(st.manufacturer);
      const models = (this._state.index.mm?.[String(mIdx)] ?? [])
        .map((i) => this._s(i))
        .sort((a, b) => a.localeCompare(b, "de"));
      if (moEl)
        this._populateNativeSelect(
          moEl,
          models,
          moEl.getAttribute("data-placeholder") || "Modell auswählen",
          false,
          st.model,
        );
    } else {
      if (moEl)
        this._populateNativeSelect(
          moEl,
          [],
          moEl.getAttribute("data-placeholder-empty") ||
            "Bitte zuerst Hersteller wählen",
          true,
          "",
        );
    }

    if (st.manufacturer && st.model) {
      const mIdx = this._getManufacturerIdx(st.manufacturer);
      const moIdx = this._getModelIdx(mIdx, st.model);
      const lights = (
        this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? []
      ).map((i) => this._s(i));
      if (lEl)
        this._populateNativeSelect(
          lEl,
          lights,
          lEl.getAttribute("data-placeholder") || "Licht-/Lasermodul auswählen",
          false,
          st.light,
        );
    } else {
      if (lEl)
        this._populateNativeSelect(
          lEl,
          [],
          lEl.getAttribute("data-placeholder-empty") ||
            "Bitte zuerst Modell wählen",
          true,
          "",
        );
    }

    if (filterEl)
      filterEl.checked = this.hasAttribute("data-filter-unavailable");

    // Alten Button entfernen falls vorhanden (bei erneutem Öffnen)
    body.querySelector("[data-hf-drawer-show]")?.remove();

    // "X Holster anzeigen" Button erstellen
    const showBtn = document.createElement("button");
    showBtn.className = "hf-drawer-show-results";
    showBtn.setAttribute("data-hf-drawer-show", "");
    showBtn.style.display = "none";

    // In den Utility-Footer vor den Reset-Button einsetzen (Fallback: Body-Ende)
    const footerEl = body.querySelector(".hf-utility-footer");
    const resetInFooter = footerEl?.querySelector("[data-hf-reset]");
    if (footerEl && resetInFooter) {
      footerEl.insertBefore(showBtn, resetInFooter);
    } else if (footerEl) {
      footerEl.insertBefore(showBtn, footerEl.firstChild);
    } else {
      body.appendChild(showBtn);
    }

    const updateShowBtn = () => {
      const st = this._state;
      const complete = st.manufacturer && st.model && st.light;
      showBtn.style.display = complete ? "" : "none";

      if (complete) {
        const count = this._state.lastEntries?.length ?? 0;
        const label =
          this.getAttribute("label-drawer-show") ||
          "{{count}} Holster anzeigen";
        showBtn.textContent = label.replace("{{count}}", count);
      }
    };

    updateShowBtn();

    showBtn.addEventListener("click", () => {
      // Filter-Switch aktivieren damit nur verfügbare Holster sichtbar sind
      this._filterUnavailable(true);
      this._inputSets.forEach((s) => {
        if (s.filterActive) s.filterActive.checked = true;
      });

      this._drawerEl.hide();

      // Zu den Cards scrollen
      const cards = this._els.cards;
      if (cards) {
        setTimeout(() => {
          cards.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    });

    // Event-Listener auf geklonten Selects
    mfEl?.addEventListener("change", (e) => {
      this._onManufacturerChange(e.target.value, -1);

      requestAnimationFrame(() => {
        this._syncDrawerSelects(body);
        updateShowBtn();
      });
    });

    moEl?.addEventListener("change", (e) => {
      this._onModelChange(e.target.value, -1);

      requestAnimationFrame(() => {
        this._syncDrawerSelects(body);
        updateShowBtn();
      });
    });

    lEl?.addEventListener("change", (e) => {
      this._onLightChange(e.target.value, -1);

      // State ist jetzt gesetzt — Button aktualisieren
      updateShowBtn();
    });

    resetEl?.addEventListener("click", () => {
      this._reset();

      requestAnimationFrame(() => {
        this._syncDrawerSelects(body);
        updateShowBtn();
      });
    });

    filterEl?.addEventListener("change", (e) => {
      this._filterUnavailable(e.detail?.checked ?? e.target.checked);

      this._inputSets.forEach((s) => {
        if (s.filterActive) {
          s.filterActive.checked = e.detail?.checked ?? e.target.checked;
        }
      });
    });
  }

  _onDrawerClose() {
    // Nichts nötig — geklonte Elemente verschwinden mit dem Drawer-Body
  }

  _syncDrawerSelects(body) {
    // Nach State-Änderungen Drawer-Selects aktuell halten
    const st = this._state;
    const mfEl = body.querySelector("[data-hf-manufacturer]");
    const moEl = body.querySelector("[data-hf-model]");
    const lEl = body.querySelector("[data-hf-light]");

    if (moEl && st.manufacturer) {
      const mIdx = this._getManufacturerIdx(st.manufacturer);

      const models = (this._state.index.mm?.[String(mIdx)] ?? [])
        .map((i) => this._s(i))
        .sort((a, b) => a.localeCompare(b, "de"));

      this._populateNativeSelect(
        moEl,
        models,
        moEl.getAttribute("data-placeholder") || "Modell auswählen",
        false,
        st.model,
      );
    } else if (moEl) {
      this._populateNativeSelect(
        moEl,
        [],
        moEl.getAttribute("data-placeholder-empty") ||
          "Bitte zuerst Hersteller wählen",
        true,
        "",
      );
    }

    if (lEl && st.manufacturer && st.model) {
      const mIdx = this._getManufacturerIdx(st.manufacturer);
      const moIdx = this._getModelIdx(mIdx, st.model);

      const lights = (
        this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? []
      ).map((i) => this._s(i));

      this._populateNativeSelect(
        lEl,
        lights,
        lEl.getAttribute("data-placeholder") || "Licht-/Lasermodul auswählen",
        false,
        st.light,
      );
    } else if (lEl) {
      this._populateNativeSelect(
        lEl,
        [],
        lEl.getAttribute("data-placeholder-empty") ||
          "Bitte zuerst Modell wählen",
        true,
        "",
      );
    }
  }

  _populateNativeSelect(el, values, placeholder, disabled, selectedValue) {
    el.innerHTML = "";

    const first = document.createElement("option");
    first.value = "";
    first.textContent = placeholder;
    el.appendChild(first);

    for (const v of values) {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;

      if (v === selectedValue) {
        opt.selected = true;
      }

      el.appendChild(opt);
    }

    el.disabled = disabled;

    if (selectedValue) {
      el.value = selectedValue;
    }
  }

  _updateSelectionStatus() {
    const els = this.querySelectorAll("[data-hf-selection-status]");

    if (!els.length) {
      return;
    }

    const st = this._state;

    let html;

    if (!st.manufacturer) {
      html =
        `<div class="hf-selection-status-unselected">` +
        `<strong>${this._esc(this._selectionEmptyLabel)}</strong>` +
        `<span>${this._esc(this._selectionEmptyHint)}</span>` +
        `</div>`;
    } else {
      const parts = [st.manufacturer, st.model].filter(Boolean);

      const light = st.light
        ? st.light === "Keines"
          ? this._noLightLabel
          : st.light
        : null;

      html =
        `<span class="hf-status-chip">` +
        `${this._esc(parts.join(" "))}` +
        `</span>`;

      if (light) {
        html +=
          `<span class="hf-status-chip">` + `${this._esc(light)}` + `</span>`;
      }
    }

    els.forEach((el) => {
      el.innerHTML = html;

      if (!st.manufacturer) {
        el.setAttribute("data-empty", "");
      } else {
        el.removeAttribute("data-empty");
      }
    });
  }

  /* ─── Tom Select lifecycle ──────────────────────────────────────── */

  _tsOptions(key, setIndex, placeholder) {
    return {
      create: false,
      allowEmptyOption: false,
      placeholder: placeholder,
      searchField: ["text"],
      sortField: false,

      // Alle Einträge anzeigen.
      // Default 50 schnitt z.B. SIG Sauer mit >50 Modellen ab.
      maxOptions: null,

      selectOnTab: true,

      onFocus() {
        const item = this.control.querySelector(".item");

        if (item) {
          item.style.display = "none";
        }

        this.setTextboxValue("");
        this.refreshOptions(false);
      },

      onItemAdd() {
        const item = this.control.querySelector(".item");

        if (item) {
          item.style.display = "";
        }

        this.setTextboxValue("");
        this.blur();
      },

      onBlur() {
        const item = this.control.querySelector(".item");

        if (item) {
          item.style.display = "";
        }
      },

      onChange: (value) => {
        if (key === "manufacturer") {
          this._onManufacturerChange(value, setIndex);
        }

        if (key === "model") {
          this._onModelChange(value, setIndex);
        }

        if (key === "light") {
          this._onLightChange(value, setIndex);
        }
      },
    };
  }

  _initAll() {
    this._inputSets.forEach((set, i) => {
      const p = {
        manufacturer:
          set.manufacturer?.getAttribute("data-placeholder") ||
          "Hersteller auswählen",

        model:
          set.model?.getAttribute("data-placeholder") || "Modell auswählen",

        light:
          set.light?.getAttribute("data-placeholder") ||
          "Licht-/Lasermodul auswählen",
      };

      if (!window.TomSelect) {
        set.manufacturer?.addEventListener("change", (e) =>
          this._onManufacturerChange(e.target.value, i),
        );

        set.model?.addEventListener("change", (e) =>
          this._onModelChange(e.target.value, i),
        );

        set.light?.addEventListener("change", (e) =>
          this._onLightChange(e.target.value, i),
        );

        return;
      }

      if (set.manufacturer) {
        this._ts[i].manufacturer = new TomSelect(
          set.manufacturer,
          this._tsOptions("manufacturer", i, p.manufacturer),
        );
      }

      if (set.model) {
        this._ts[i].model = new TomSelect(
          set.model,
          this._tsOptions("model", i, p.model),
        );
      }

      if (set.light) {
        this._ts[i].light = new TomSelect(
          set.light,
          this._tsOptions("light", i, p.light),
        );
      }
    });
  }

  _destroyAll() {
    this._ts.forEach((ts) => {
      for (const t of Object.values(ts)) {
        if (t) {
          t.destroy();
        }
      }
    });

    this._ts = this._inputSets.map(() => ({
      manufacturer: null,
      model: null,
      light: null,
    }));
  }

  /* ─── Populate helper — alle Desktop-Sets synchron befüllen ────── */

  _populate(key, placeholder, values, disabled) {
    this._inputSets.forEach((set, i) => {
      const ts = this._ts[i][key];
      const el = set[key];

      if (ts) {
        ts.clear(true);
        ts.clearOptions();

        for (const v of values) {
          ts.addOption({
            value: v,
            text: v,
          });
        }

        ts.setValue("", true);

        if (disabled) {
          ts.disable();
        } else {
          ts.enable();
        }

        ts.refreshOptions(false);
      } else if (el) {
        el.innerHTML = "";

        const first = document.createElement("option");
        first.value = "";
        first.textContent = placeholder;
        el.appendChild(first);

        for (const v of values) {
          const opt = document.createElement("option");
          opt.value = v;
          opt.textContent = v;
          el.appendChild(opt);
        }

        el.disabled = disabled;
      }
    });
  }

  _syncValue(key, value) {
    this._inputSets.forEach((set, i) => {
      const ts = this._ts[i][key];
      const el = set[key];

      if (ts) {
        ts.setValue(value, true);

        const item = ts.control.querySelector(".item");

        if (item) {
          item.style.display = value ? "" : "none";
        }
      } else if (el) {
        el.value = value;
      }
    });
  }

  _setSubmitState(enabled) {
    this._inputSets.forEach((set) => {
      if (!set.submit) {
        return;
      }

      set.submit.classList.toggle("hf-submit--disabled", !enabled);

      set.submit.disabled = !enabled;
    });
  }

  /* ─── Data loading ──────────────────────────────────────────────── */

  async _loadData() {
    this._setStatus("Daten werden geladen…");

    try {
      const indexRes = await fetch(this._indexUrl);

      if (!indexRes.ok) {
        throw new Error(`Index-Datei nicht erreichbar (${indexRes.status})`);
      }

      const index = await indexRes.json();

      this._validateIndex(index);

      this._state.index = index;

      this._bootSelects();

      /*
       * URL-Parameter werden erst NACH dem Laden und Validieren
       * des Index ausgewertet.
       *
       * Es gelangen ausschliesslich kanonische Werte aus dem
       * Index in den State.
       *
       * Ungültige Werte werden vollständig verworfen.
       */
      const appliedUrlSelection = this._applyUrlSelection();

      if (!appliedUrlSelection) {
        this._renderCards([]);
        this._setStatus("");
      }

      this.dispatchEvent(
        new CustomEvent("holster-finder:ready", {
          bubbles: true,
        }),
      );

      /*
       * Bei einer gültigen URL-Vorauswahl bleibt der von
       * _findAndRender() gesetzte Ergebnis-Status erhalten.
       */

      // this._initVideoHover();
    } catch (err) {
      this._setStatus(`Fehler: ${err.message}`);

      const dot = this.statusDot?.querySelector(".hf-status-dot");

      if (dot) {
        dot.classList.remove("online");
        dot.classList.add("offline");
      }

      console.error("[holster-finder]", err);
    }
  }

  /* ─── Index / URL validation ───────────────────────────────────── */

  _validateIndex(index) {
    const isObject = (value) =>
      value !== null && typeof value === "object" && !Array.isArray(value);

    if (!isObject(index) || index.v !== 5) {
      throw new Error("Ungültige oder nicht unterstützte Index-Version");
    }

    if (
      !Array.isArray(index.s) ||
      !Array.isArray(index.h) ||
      !Array.isArray(index.f) ||
      !Array.isArray(index.m) ||
      !isObject(index.mm) ||
      !isObject(index.ml) ||
      !isObject(index.r) ||
      !isObject(index.mr)
    ) {
      throw new Error("Ungültige Index-Struktur");
    }
  }

  _readUrlSelection() {
    /*
     * Begrenzung der gesamten Query verhindert,
     * dass extrem grosse URLs unnötig verarbeitet werden.
     *
     * Andere Query-Parameter dürfen vorhanden sein.
     * Ausgewertet werden ausschließlich:
     *
     * manufacturer
     * model
     * light
     */
    if (window.location.search.length > 4096) {
      return {
        present: true,
        valid: false,
      };
    }

    const params = new URLSearchParams(window.location.search);

    const keys = ["manufacturer", "model", "light"];

    const hasAny = keys.some((key) => params.has(key));

    if (!hasAny) {
      return {
        present: false,
        valid: false,
      };
    }

    const values = {};

    for (const key of keys) {
      const all = params.getAll(key);

      /*
       * Alle drei Parameter müssen exakt einmal
       * vorhanden sein.
       *
       * Doppelte Parameter werden nicht nach
       * "first wins" oder "last wins" interpretiert.
       */
      if (all.length !== 1) {
        return {
          present: true,
          valid: false,
        };
      }

      const value = all[0];

      /*
       * Die echten Werte werden später zusätzlich
       * per exaktem Match gegen den Index geprüft.
       *
       * Diese Guards begrenzen nur unnötige Eingaben
       * und verwerfen:
       *
       * - leere Werte
       * - extrem lange Werte
       * - führenden/trailing Whitespace
       * - Steuerzeichen
       */
      if (
        typeof value !== "string" ||
        value.length === 0 ||
        value.length > 256 ||
        value !== value.trim() ||
        /[\u0000-\u001F\u007F-\u009F]/.test(value)
      ) {
        return {
          present: true,
          valid: false,
        };
      }

      values[key] = value;
    }

    return {
      present: true,
      valid: true,
      values,
    };
  }

  _validateUrlSelection(values) {
    if (!values || typeof values !== "object") {
      return null;
    }

    /*
     * Hersteller wird ausschließlich gegen die
     * existierenden Hersteller des Index geprüft.
     */
    const mIdx = this._getManufacturerIdx(values.manufacturer);

    if (mIdx === null) {
      return null;
    }

    /*
     * Modell wird ausschließlich innerhalb des
     * zuvor validierten Herstellers geprüft.
     */
    const moIdx = this._getModelIdx(mIdx, values.model);

    if (moIdx === null) {
      return null;
    }

    /*
     * Licht wird ausschließlich innerhalb der
     * Kombination Hersteller + Modell geprüft.
     */
    const lIdx = this._getLightIdx(mIdx, moIdx, values.light);

    if (lIdx === null) {
      return null;
    }

    /*
     * End-to-End-Prüfung:
     *
     * Die vollständige Kombination muss im finalen
     * Result-Index mindestens ein Holster besitzen.
     *
     * Dadurch reicht es nicht, dass drei Einzelwerte
     * irgendwo im Index vorhanden sind.
     */
    const resultKey = this._rKey(mIdx, moIdx, lIdx);

    const entries = this._state.index.r?.[resultKey];

    if (!Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    /*
     * WICHTIG:
     *
     * Niemals die Strings aus der URL zurückgeben.
     *
     * Stattdessen werden ausschließlich die
     * kanonischen Werte aus dem validierten Index
     * verwendet.
     */
    return {
      manufacturer: this._s(mIdx),

      model: this._s(moIdx),

      light: this._s(lIdx),
    };
  }

  _applyUrlSelection() {
    const parsed = this._readUrlSelection();

    /*
     * Keine Finder-Parameter vorhanden:
     * normale leere Finder-Ansicht.
     */
    if (!parsed.present) {
      return false;
    }

    /*
     * Finder-Parameter vorhanden, aber formal ungültig.
     */
    if (!parsed.valid) {
      console.warn("[holster-finder] Ungültige URL-Auswahl verworfen.");

      return false;
    }

    /*
     * Formell korrekt, aber Kombination existiert
     * nicht im Finder-Index.
     */
    const selection = this._validateUrlSelection(parsed.values);

    if (!selection) {
      console.warn(
        "[holster-finder] Nicht vorhandene URL-Kombination verworfen.",
      );

      return false;
    }

    /*
     * Die normalen Change-Handler werden bewusst
     * wiederverwendet.
     *
     * Dadurch laufen:
     *
     * - URL-Vorauswahl
     * - manuelle Auswahl
     *
     * durch exakt dieselbe Finder-Logik.
     */
    this._onManufacturerChange(selection.manufacturer, -1);

    this._onModelChange(selection.model, -1);

    this._onLightChange(selection.light, -1);

    return true;
  }

  /* ─── Index helpers ─────────────────────────────────────────────── */

  _s(i) {
    return this._state.index.s[i] ?? "";
  }

  _h(i) {
    return this._state.index.h[i] ?? "";
  }

  _f(i) {
    return this._state.index.f[i] ?? "";
  }

  _mmKey(mIdx, moIdx) {
    return `${mIdx}|${moIdx}`;
  }

  _rKey(mIdx, moIdx, lIdx) {
    return `${mIdx}|${moIdx}|${lIdx}`;
  }

  _manufacturerIndices() {
    return this._state.index.m ?? [];
  }

  _getManufacturerIdx(label) {
    for (const idx of this._manufacturerIndices()) {
      if (this._s(idx) === label) {
        return idx;
      }
    }

    return null;
  }

  _getModelIdx(mIdx, label) {
    const modelIndices = this._state.index.mm?.[String(mIdx)] ?? [];

    for (const idx of modelIndices) {
      if (this._s(idx) === label) {
        return idx;
      }
    }

    return null;
  }

  _getLightIdx(mIdx, moIdx, label) {
    const lightIndices = this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? [];

    for (const idx of lightIndices) {
      if (this._s(idx) === label) {
        return idx;
      }
    }

    return null;
  }

  /* ─── Boot ──────────────────────────────────────────────────────── */

  _bootSelects() {
    const manufacturers = this._manufacturerIndices()
      .map((i) => this._s(i))
      .sort((a, b) => a.localeCompare(b, "de"));

    this._initAll();

    const first = this._inputSets[0];

    const p = {
      manufacturer:
        first.manufacturer?.getAttribute("data-placeholder") ||
        "Hersteller auswählen",

      model:
        first.model?.getAttribute("data-placeholder") ||
        "Bitte zuerst Hersteller wählen",

      light:
        first.light?.getAttribute("data-placeholder") ||
        "Bitte zuerst Modell wählen",
    };

    this._populate("manufacturer", p.manufacturer, manufacturers, false);

    this._populate("model", p.model, [], true);

    this._populate("light", p.light, [], true);
  }

  _reset() {
    const st = this._state;

    st.manufacturer = "";
    st.model = "";
    st.light = "";
    st.lastEntries = [];

    this._ts.forEach((ts) => {
      ts.manufacturer?.clear();
      ts.model?.clear();
      ts.light?.clear();
    });

    const first = this._inputSets[0];

    const p = {
      model:
        first.model?.getAttribute("data-placeholder-empty") ||
        "Bitte zuerst Hersteller wählen",

      light:
        first.light?.getAttribute("data-placeholder-empty") ||
        "Bitte zuerst Modell wählen",
    };

    this._populate("model", p.model, [], true);

    this._populate("light", p.light, [], true);

    this._setStatus("");

    this._setSubmitState(false);

    const cards = this._els.cards?.querySelectorAll("[data-holster-id]");

    if (cards) {
      for (const card of cards) {
        card.href = card.dataset.originalHref || "#";

        card.style.pointerEvents = "";
      }
    }

    this._inputSets.forEach((set) => {
      if (set.filterActive) {
        set.filterActive.checked = false;
      }
    });

    this.removeAttribute("data-filter-unavailable");

    this._renderCards([]);

    this._updateSelectionStatus();
  }

  _filterUnavailable(hide) {
    this.toggleAttribute("data-filter-unavailable", hide);

    if (this._state.lastEntries?.length > 0) {
      this._renderCards(this._state.lastEntries);
    }
  }

  /* ─── Video hover ───────────────────────────────────────────────── */

  _initVideoHover() {
    const container = this._els.cards;

    if (!container) {
      return;
    }

    container
      .querySelectorAll(".hf-card:has(video.hf-card-video)")
      .forEach((card) => {
        const video = card.querySelector("video.hf-card-video");

        if (!video) {
          return;
        }

        card.addEventListener("mouseenter", () => {
          if (video.readyState === 0) {
            video.load();
          }

          const tryPlay = () => video.play().catch(() => {});

          if (video.readyState >= 3) {
            tryPlay();
          } else {
            video.addEventListener("canplay", tryPlay, {
              once: true,
            });
          }
        });

        card.addEventListener("mouseleave", () => {
          video.pause();
          video.currentTime = 0;
        });
      });
  }

  /* ─── Change handlers ───────────────────────────────────────────── */

  _onManufacturerChange(value, sourceSetIndex) {
    const st = this._state;

    st.manufacturer = value || "";

    st.model = "";
    st.light = "";

    const first = this._inputSets[0];

    const p = {
      model:
        first.model?.getAttribute("data-placeholder-empty") ||
        "Bitte zuerst Hersteller wählen",

      light:
        first.light?.getAttribute("data-placeholder-empty") ||
        "Bitte zuerst Modell wählen",
    };

    this._populate("model", p.model, [], true);

    this._populate("light", p.light, [], true);

    this._renderCards([]);

    this._setStatus("");

    this._setSubmitState(false);

    this._updateSelectionStatus();

    /*
     * Desktop-Sets synchronisieren.
     * Die Quelle selbst wird ausgelassen.
     */
    this._inputSets.forEach((set, i) => {
      if (i === sourceSetIndex) {
        return;
      }

      const ts = this._ts[i].manufacturer;

      if (ts) {
        ts.setValue(value, true);
      } else if (set.manufacturer) {
        set.manufacturer.value = value;
      }
    });

    if (!st.manufacturer) {
      return;
    }

    const mIdx = this._getManufacturerIdx(st.manufacturer);

    const models = (this._state.index.mm?.[String(mIdx)] ?? [])
      .map((i) => this._s(i))
      .sort((a, b) => a.localeCompare(b, "de"));

    const pModel =
      first.model?.getAttribute("data-placeholder") || "Modell auswählen";

    this._populate("model", pModel, models, false);
  }

  _onModelChange(value, sourceSetIndex) {
    const st = this._state;

    st.model = value || "";

    st.light = "";

    const first = this._inputSets[0];

    const pLight =
      first.light?.getAttribute("data-placeholder-empty") ||
      "Bitte zuerst Modell wählen";

    this._populate("light", pLight, [], true);

    this._setStatus("");

    this._setSubmitState(false);

    /*
     * Desktop-Sets synchronisieren.
     */
    this._inputSets.forEach((set, i) => {
      if (i === sourceSetIndex) {
        return;
      }

      const ts = this._ts[i].model;

      if (ts) {
        ts.setValue(value, true);
      } else if (set.model) {
        set.model.value = value;
      }
    });

    if (!st.manufacturer || !st.model) {
      this._renderCards([]);
      return;
    }

    const mIdx = this._getManufacturerIdx(st.manufacturer);

    const moIdx = this._getModelIdx(mIdx, st.model);

    const lights = (this._state.index.ml?.[this._mmKey(mIdx, moIdx)] ?? []).map(
      (i) => this._s(i),
    );

    const pLightActive =
      first.light?.getAttribute("data-placeholder") ||
      "Licht-/Lasermodul auswählen";

    this._populate("light", pLightActive, lights, false);

    /*
     * Falls ausschließlich "Keines" möglich ist,
     * wird es automatisch ausgewählt.
     */
    if (lights.length === 1 && lights[0] === "Keines") {
      this._syncValue("light", "Keines");

      st.light = "Keines";

      this._setSubmitState(true);

      this._findAndRender();
    } else {
      this._renderCards([]);
    }

    this._updateSelectionStatus();
  }

  _onLightChange(value, sourceSetIndex) {
    const st = this._state;

    st.light = value || "";

    this._setSubmitState(!!st.light);

    /*
     * Desktop-Sets synchronisieren.
     */
    this._inputSets.forEach((set, i) => {
      if (i === sourceSetIndex) {
        return;
      }

      const ts = this._ts[i].light;

      if (ts) {
        ts.setValue(value, true);
      } else if (set.light) {
        set.light.value = value;
      }
    });

    if (!st.manufacturer || !st.model || !st.light) {
      this._renderCards([]);
      this._setStatus("");
      return;
    }

    this._findAndRender();

    this._updateSelectionStatus();
  }

  /* ─── Lookup & render ───────────────────────────────────────────── */

  _findAndRender() {
    const st = this._state;

    const mIdx = this._getManufacturerIdx(st.manufacturer);

    const moIdx = this._getModelIdx(mIdx, st.model);

    const lIdx = this._getLightIdx(mIdx, moIdx, st.light);

    if (mIdx === null || moIdx === null || lIdx === null) {
      this._setStatus("Keine Einträge für diese Kombination im Index.");

      this._renderCards([]);

      return;
    }

    const entries = this._state.index.r?.[this._rKey(mIdx, moIdx, lIdx)] ?? [];

    this._setStatus(
      `${entries.length} Holster gefunden für: ` +
        `${st.manufacturer} · ${st.model} · ${st.light}`,
    );

    this._state.lastEntries = entries;

    /*
     * Alle 3 Felder gewählt:
     *
     * Filter automatisch aktivieren,
     * sodass nur kompatible Holster sichtbar bleiben.
     */
    this.setAttribute("data-filter-unavailable", "");

    this._inputSets.forEach((set) => {
      if (set.filterActive) {
        set.filterActive.checked = true;
      }
    });

    this._renderCards(entries);
  }

  _buildUrl(entry) {
    const st = this._state;

    const [
      holsterIdx,
      rawModelIdx,
      rawLightIdx,
      lightFieldIdx,
      mfFieldIdx,
      modelFieldIdx,
    ] = entry;

    const holsterId = this._h(holsterIdx);

    const card = this._els.cards?.querySelector(
      `[data-holster-id="${holsterId}"]`,
    );

    const baseUrl = card?.dataset.originalHref;

    if (!baseUrl) {
      return "#";
    }

    const url = new URL(baseUrl);

    const mfField = this._f(mfFieldIdx);

    const modelField = this._f(modelFieldIdx);

    const lightField = this._f(lightFieldIdx);

    const rawModel = this._s(rawModelIdx);

    const rawLight = this._s(rawLightIdx);

    /*
     * Roher Hersteller-Wert für den Deep-Link
     * kommt aus der mr-Map des Index.
     *
     * display -> DPO-Feldwert
     */
    const mrMap = this._state.index?.mr || {};

    const rawMf = mrMap[st.manufacturer] ?? st.manufacturer;

    /*
     * DPO-Feldnamen dürfen ausschließlich dem Format
     * F + Integer entsprechen.
     *
     * Dadurch können manipulierte Feldnamen nicht als
     * beliebige Query-Parameter verwendet werden.
     */
    const isFieldKey = (value) =>
      typeof value === "string" && /^F\d+$/.test(value);

    if (isFieldKey(mfField) && rawMf) {
      url.searchParams.set(mfField, rawMf);
    }

    if (isFieldKey(modelField) && rawModel) {
      url.searchParams.set(modelField, rawModel);
    }

    if (isFieldKey(lightField) && rawLight) {
      url.searchParams.set(lightField, rawLight);
    }

    return url
      .toString()
      .replace(/\+/g, "%20")
      .replace(/%28/g, "(")
      .replace(/%29/g, ")");
  }

  _renderCards(entries) {
    const container = this._els.cards;

    if (!container) {
      return;
    }

    const availableIds = new Set(entries.map((entry) => this._h(entry[0])));

    const hasSearch = entries.length > 0 || this._state.light;

    const cards = container.querySelectorAll("[data-holster-id]");

    for (const card of cards) {
      const holsterId = card.dataset.holsterId;

      const isAvailable = !hasSearch || availableIds.has(holsterId);

      card.classList.toggle("hf-card--available", hasSearch && isAvailable);

      card.classList.toggle("hf-card--unavailable", hasSearch && !isAvailable);

      if (isAvailable) {
        const entry = entries.find((item) => this._h(item[0]) === holsterId);

        const url = entry
          ? this._buildUrl(entry)
          : card.dataset.originalHref || "#";

        card.href = url;

        card.style.pointerEvents = "";

        /*
         * Drawer-CTA mit derselben Deep-Link-URL versorgen.
         */
        document.dispatchEvent(
          new CustomEvent("holster:available", {
            detail: {
              holsterId,
              url,
            },
          }),
        );
      } else {
        card.href = "#";

        card.style.pointerEvents = "none";

        /*
         * Drawer-CTA auf Basis-URL zurücksetzen,
         * damit kein veralteter Deep-Link bestehen bleibt.
         */
        document.dispatchEvent(
          new CustomEvent("holster:available", {
            detail: {
              holsterId,

              url: card.dataset.originalHref || "#",
            },
          }),
        );
      }
    }

    const groups = container.querySelectorAll(".hf-group");

    /*
     * Selektions-Text für Empty-State.
     *
     * Beispiel:
     * OPOS Venator · Glock GTL-2
     */
    const stSel = this._state;

    let emptyCombo = [stSel.manufacturer, stSel.model]
      .filter(Boolean)
      .join(" ");

    if (stSel.light && stSel.light !== "Keines") {
      emptyCombo += " · " + stSel.light;
    }

    for (const group of groups) {
      const carryKey = group.getAttribute("data-hf-group");

      const heading = container.querySelector(
        `.hf-group-heading[data-hf-group-heading="${carryKey}"]`,
      );

      const wrapper = heading
        ? heading.closest(".hf-group-heading-wrapper")
        : null;

      if (!wrapper) {
        continue;
      }

      const countEl = wrapper.querySelector(".hf-group-heading-count");

      const total = group.querySelectorAll("[data-holster-id]").length;

      const available = group.querySelectorAll(".hf-card--available").length;

      const hidden = group.querySelectorAll(".hf-card--unavailable").length;

      const filterOn = this.hasAttribute("data-filter-unavailable");

      if (heading) {
        /*
        heading.classList.toggle(
          "hf-group-heading--unavailable",
          hasSearch && available === 0
        );
        */
      }

      if (countEl) {
        if (!hasSearch) {
          countEl.textContent = this._countLabelTotal.replace(
            "{{count}}",
            total,
          );
        } else if (available === 0) {
          countEl.textContent = this._countLabelZero;
        } else if (filterOn && hidden > 0) {
          countEl.textContent =
            this._countLabelFound.replace("{{count}}", available) +
            " " +
            this._countLabelHidden.replace("{{count}}", hidden);
        } else {
          countEl.textContent = this._countLabelFound.replace(
            "{{count}}",
            available,
          );
        }
      }

      /*
       * No-Match-Hinweis:
       *
       * nur anzeigen, wenn Filter aktiv ist und
       * keine verfügbaren Modelle in der Kategorie
       * vorhanden sind.
       */
      const emptyEl = container.querySelector(
        `.hf-group-no-match[data-hf-group-empty="${carryKey}"]`,
      );

      if (emptyEl) {
        const showEmpty = hasSearch && filterOn && available === 0;

        emptyEl.style.display = showEmpty ? "block" : "none";

        if (showEmpty) {
          const textEl = emptyEl.querySelector("[data-hf-empty-text]");

          if (textEl) {
            textEl.innerHTML = this._emptyLabel.replace(
              /\{\{\s*selection\s*\}\}/g,
              `<span>${this._esc(emptyCombo)}</span>`,
            );
          }
        }
      }
    }
  }

  /* ─── Utilities ─────────────────────────────────────────────────── */

  _setStatus(msg) {
    if (this._els.status) {
      this._els.status.textContent = msg;
    }
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}

if (!customElements.get("holster-finder")) {
  customElements.define("holster-finder", HolsterFinder);
}
